/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXRefValue } from '@cloudio-saas/statex';
import {
  IconButton,
  Paper,
  PaperProps,
  Theme,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getManager } from 'componentregistry';
import { DragControls, motion, useDragControls } from 'framer-motion';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React from 'react';
import { itemWithDefaultValues } from 'registry/Components';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
  },
  label: {
    textAlign: 'end',
    paddingRight: theme.spacing(1),
    minWidth: 200,
  },
}));

export function DraggablePaperComponent({
  dragControls,
  ...props
}: PaperProps & { dragControls: DragControls }) {
  const ref = useStateXRefValue([
    'pageRef',
  ]) as React.MutableRefObject<HTMLDivElement>;
  return (
    <motion.div
      drag
      dragListener={false}
      dragConstraints={ref}
      dragControls={dragControls}
      dragMomentum={false}
    >
      <Paper {...props} />
    </motion.div>
  );
}

export default function ExamineItemProps({
  onClose,
  item: { appUid, pageId, itemId },
}: {
  item: { appUid: string; pageId: string; itemId: string };
  onClose: () => void;
}): React.ReactElement | null {
  const classes = useStyles();
  const platform = usePlatform();
  const item = platform.getItem(appUid, pageId, itemId);
  if (!item) {
    throw Error(`Missing item with ID ${itemId}!`); // this will never happen
  }
  const itemWithDefaults = itemWithDefaultValues(item);
  const mgr = getManager(item.type);
  const dragControls = useDragControls();

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    dragControls.start(event);
  }

  return (
    <Dialog
      onClose={onClose}
      open
      maxWidth="xl"
      PaperComponent={(props: PaperProps) => (
        <DraggablePaperComponent {...props} dragControls={dragControls} />
      )}
    >
      <DialogTitle
        onPointerDown={startDrag}
        sx={{ m: 0, p: 2, pr: '72px', cursor: 'move' }}
      >
        {mgr?.name} Properties
        <IconButton
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 4,
          pt: 0,
          userSelect: 'text',
          maxHeight: window.innerHeight - 100,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <table>
          <tbody>
            {Object.keys(itemWithDefaults.props)
              .sort()
              .map((key) => {
                let value = itemWithDefaults.props[key];
                if (typeof value !== 'string') {
                  // @ts-ignore value is never
                  value = JSON.stringify(value);
                }
                // @ts-ignore dynamic props
                const def = mgr.props[key];
                return (
                  <tr key={key}>
                    <td className={classes.label}>
                      {/* @ts-ignore dynamic props */}
                      {def?.label || key} {item.props[key] ? '*' : ''}
                    </td>
                    <td>{value}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
